import {
  CommutingTripSummary,
  Employee,
  Event,
  Partner,
  TEmployeeTransportHabits,
  TPartnerTypeWithHolding,
  TUserType,
  User,
  UserGeogroup,
  UserPlace,
} from '@geovelo-frontends/commons';
import { RefObject, createContext, createRef } from 'react';

export type TPermissionKey =
  | 'editorializedChallengesEnabled'
  | 'customChallengePeriodLimited'
  | 'allJerseysEnabled'
  | 'mobilitySurveyEnabled'
  | 'mobilitySurveyResponsesReadEnabled'
  | 'newsPostsCreationAndUpdateEnabled'
  | 'accessibilityEnabled'
  | 'advancedStatsEnabled'
  | 'fmdEnabled'
  | 'fullLeaderboardsEnabled';

export type TPermissions = { [key in TPermissionKey]: boolean };

export const offersMap: {
  [key in TPartnerTypeWithHolding]: { key: string; label: string; permissions: TPermissions };
} = {
  premium: {
    key: 'premium',
    label: 'Premium\u00a0💎',
    permissions: {
      editorializedChallengesEnabled: true,
      customChallengePeriodLimited: false,
      allJerseysEnabled: true,
      mobilitySurveyEnabled: true,
      mobilitySurveyResponsesReadEnabled: true,
      newsPostsCreationAndUpdateEnabled: true,
      accessibilityEnabled: true,
      advancedStatsEnabled: true,
      fmdEnabled: true,
      fullLeaderboardsEnabled: true,
    },
  },
  free: {
    key: 'free',
    label: 'Gratuit\u00a0✅',
    permissions: {
      editorializedChallengesEnabled: false,
      customChallengePeriodLimited: true,
      allJerseysEnabled: false,
      mobilitySurveyEnabled: true,
      mobilitySurveyResponsesReadEnabled: false,
      newsPostsCreationAndUpdateEnabled: true,
      accessibilityEnabled: true,
      advancedStatsEnabled: false,
      fmdEnabled: false,
      fullLeaderboardsEnabled: false,
    },
  },
  standard: {
    key: 'standard',
    label: 'Standard\u00a0⭐️',
    permissions: {
      editorializedChallengesEnabled: true,
      customChallengePeriodLimited: false,
      allJerseysEnabled: true,
      mobilitySurveyEnabled: true,
      mobilitySurveyResponsesReadEnabled: true,
      newsPostsCreationAndUpdateEnabled: true,
      accessibilityEnabled: true,
      advancedStatsEnabled: true,
      fmdEnabled: false,
      fullLeaderboardsEnabled: true,
    },
  },
  holding: {
    key: 'holding',
    label: 'Holding',
    permissions: {
      editorializedChallengesEnabled: false,
      customChallengePeriodLimited: false,
      allJerseysEnabled: true,
      mobilitySurveyEnabled: false,
      mobilitySurveyResponsesReadEnabled: false,
      newsPostsCreationAndUpdateEnabled: true,
      accessibilityEnabled: false,
      advancedStatsEnabled: true,
      fmdEnabled: false,
      fullLeaderboardsEnabled: true,
    },
  },
};

export const getStartedEmployeeSteps = ['homeWorkJourney', 'profilePicture', 'team'] as const;

export type TGetStartedEmployeeSteps = (typeof getStartedEmployeeSteps)[number];

export interface IUserContext {
  commutingTrips?: CommutingTripSummary[];
  creationReasonDialogSeen?: boolean;
  current?: User | null;
  employee?: Employee | null;
  employees?: Array<{ employee: Employee; partner: Partner }>;
  getStartedProgression?: {
    allDone: boolean;
    doneCount: number;
    ignoredSteps: { [key in TGetStartedEmployeeSteps]?: boolean };
    stepsDone: { [key in TGetStartedEmployeeSteps]: boolean };
  };
  home?: UserPlace | null;
  isRegister?: boolean;
  site?: UserGeogroup | null;
  team?: UserGeogroup | null;
  transportHabits?: TEmployeeTransportHabits | null;
  works?: UserPlace[];
}

export const getStartedAdminSteps = ['accountConfig', 'firstChallenge', 'discoverOffers'] as const;

export type TGetStartedAdminSteps = (typeof getStartedAdminSteps)[number];

export type TGetStartedAdminSubsteps = {
  accountConfig: 'logo' | 'invitations';
  firstChallenge: 'firstChallenge';
  discoverOffers: 'discoverOffers';
};

export type TGetStartedAdminStepProgression<TSubsteps> = {
  allDone: boolean;
  doneCount: number;
  substeps: Array<{ done: boolean; key: TSubsteps }>;
};

export type TGetStartedAdminProgression = {
  allDone: boolean;
  doneCount: number;
  steps: {
    [key in TGetStartedAdminSteps]: TGetStartedAdminStepProgression<TGetStartedAdminSubsteps[key]>;
  };
};

export interface IPartnerContext {
  current?: Partner | null;
  eventDialogOpen?: boolean;
  firstMembersAddedDialogOpenRef?: RefObject<boolean>;
  isFrenchGeogroup?: boolean;
  getStartedProgression?: TGetStartedAdminProgression;
  mobilitySurveyDialogOpen?: boolean;
  permissions: { [key in TPermissionKey]: boolean };
  type?: TPartnerTypeWithHolding | null;
}

interface IAppActions {
  openEventDialog: (open: boolean) => void;
  openMobilitySurveyDialog: (open: boolean) => void;
  selectCompany: (employee: Employee, partner: Partner) => void;
  setCurrentEvent: (event: Event | null | undefined) => void;
  setCurrentUser: (user?: User | null) => void;
  setCurrentPartner: (partner?: Partner | null) => void;
  setIsFrenchGeogroup: (value: boolean) => void;
  setPartnerPermissions: (permissions: { [key in TPermissionKey]: boolean }) => void;
  setPartnerType: (type?: TPartnerTypeWithHolding | null) => void;
  setUserCommutingTrips: (trips?: CommutingTripSummary[]) => void;
  setUserCreationReasonDialogSeen: (seen?: boolean) => void;
  setUserEmployee: (employee?: Employee | null) => void;
  setUserEmployees: (employees?: Array<{ employee: Employee; partner: Partner }>) => void;
  setUserHome: (home?: UserPlace | null) => void;
  setUserIsRegister: (isRegister?: boolean) => void;
  setUserSite: (site?: UserGeogroup | null) => void;
  setUserTeam: (team?: UserGeogroup | null) => void;
  setUserTransportHabits: (habits?: TEmployeeTransportHabits | null) => void;
  setUserWorks: (works?: UserPlace[]) => void;
}

export type TPageKey =
  | 'getStarted'
  | 'home'
  | 'challenges'
  | 'leaderboards'
  | 'news'
  | 'stats'
  | 'holdingOverview'
  | 'fmdConfig'
  | 'fmd'
  | 'fmdEmployee'
  | 'fmdCertificates'
  | 'fmdInfo'
  | 'incitation'
  | 'covelotaf'
  | 'bfgEvent';

export const alphaPages: TPageKey[] = [];

interface IAppContext {
  actions: IAppActions;
  app: {
    currentEvent: Event | null | undefined;
    pageVisibility: { [key in TPageKey]?: 'alpha' };
  };
  history: RefObject<string[] | null>;
  user: IUserContext;
  partner: IPartnerContext;
}

export const AppContext = createContext<IAppContext>({
  history: createRef(),
  app: {
    currentEvent: undefined,
    pageVisibility: {},
  },
  user: {},
  partner: { permissions: offersMap.free.permissions },
  actions: {
    setCurrentUser: () => undefined,
    selectCompany: () => undefined,
    setCurrentEvent: () => undefined,
    setCurrentPartner: () => undefined,
    setPartnerType: () => undefined,
    setIsFrenchGeogroup: () => undefined,
    setPartnerPermissions: () => undefined,
    openEventDialog: () => undefined,
    openMobilitySurveyDialog: () => undefined,
    setUserIsRegister: () => undefined,
    setUserEmployee: () => undefined,
    setUserEmployees: () => undefined,
    setUserTeam: () => undefined,
    setUserSite: () => undefined,
    setUserHome: () => undefined,
    setUserWorks: () => undefined,
    setUserCommutingTrips: () => undefined,
    setUserTransportHabits: () => undefined,
    setUserCreationReasonDialogSeen: () => undefined,
  },
});

export type { TUserType };
